import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, HostListener, inject, OnDestroy, Output } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import {
  AppComms,
  E360HeaderConfig,
  EnvService,
  LoggerService,
  ModalService,
  Module,
  ModuleType,
  ScopeType,
  SessionToken,
  SpinnerComponent,
  SpinnerService,
  UserActivity,
  UserActivityBuilderService,
  UserService
} from '@enliven/angular';
import {
  finalize,
  noop,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  tap,
  timer,
  catchError
} from 'rxjs';
import { SignOutOpts } from 'src/app/models/sign-out-opts.model';
import { AmplifyService } from 'src/app/services/amplify.service';
import { CognitoService } from 'src/app/services/cognito.service';
import { DataService } from 'src/app/services/data.service';
import { E360SessionService } from 'src/app/services/e360-session.service';
import { E360UserService } from 'src/app/services/e360-user.service';
import { HeaderService } from 'src/app/services/header.service';
import { DeepLink, NavigationOptions, NavigationService } from 'src/app/services/navigation.service';
import { OneTrustCookieBannerService } from 'src/app/services/one-trust-cookie-banner.service';
import { SessionSharingService } from 'src/app/services/session-sharing.service';
import { StateService } from 'src/app/services/state.service';
import { UrlParamService } from 'src/app/services/url-param.service';
import { HeaderComponent } from '../header/header.component';
import { LeftNavComponent } from '../left-nav/left-nav.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'e360-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: true,
  imports: [LeftNavComponent, NgClass, RouterOutlet, HeaderComponent, SpinnerComponent, TranslateModule]
})
export class HomeComponent implements AfterViewInit, OnDestroy {
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly cognitoService: CognitoService = inject(CognitoService);
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly router: Router = inject(Router);
  private readonly e360UserService: E360UserService = inject(E360UserService);
  private readonly navigationService: NavigationService = inject(NavigationService);
  private readonly amplifyService: AmplifyService = inject(AmplifyService);
  private readonly dataService: DataService = inject(DataService);
  private readonly spinnerService: SpinnerService = inject(SpinnerService);
  private readonly stateService: StateService = inject(StateService);
  private readonly oneTrustCookieBannerService: OneTrustCookieBannerService = inject(OneTrustCookieBannerService);
  private readonly userService: UserService = inject(UserService);
  private readonly userActivityBuilder: UserActivityBuilderService = inject(UserActivityBuilderService);
  private readonly urlParams: UrlParamService = inject(UrlParamService);
  private readonly modalService: ModalService = inject(ModalService);
  private readonly sessionSharingService: SessionSharingService = inject(SessionSharingService);
  private readonly logger: LoggerService = inject(LoggerService);
  public readonly headerService: HeaderService = inject(HeaderService);
  public readonly envService: EnvService = inject(EnvService);

  @Output() toggleMenu = new EventEmitter();

  private readonly _unsubscribe = new Subject();

  private SESSION_MAX_INACTIVITY_SECONDS: number;
  private _inactivityTimerSource$: Observable<number>;
  private _inactivityTimer: Subscription;

  private SESSION_MAX_SECONDS: number;
  private _maxSessionLifetimeTimerSource$: Observable<number>;

  constructor() {
    // Initialize session sharing for cross-tab communication
    // This allows this tab to respond to session requests from other tabs
    this.sessionSharingService.initializeChannel();

    this.route.data.pipe(takeUntil(this._unsubscribe)).subscribe(() => {
      this.oneTrustCookieBannerService.loadScripts();
      if (this.e360SessionService.appReload) return;
      const { firstName, lastName } = this.e360UserService.user;
      const { selectedOrg, selectedOrgStore } = this.stateService;
      const loginType: string = this.e360SessionService.isSSO ? 'sso' : 'direct';
      const activityMsg: string = `${firstName} ${lastName} logged into e360 via ${loginType} login`;
      const activity: UserActivity = this.userActivityBuilder.buildUserActivityEvent(
        'LOGIN',
        activityMsg,
        true,
        'e360',
        this.e360UserService.user,
        selectedOrg,
        selectedOrgStore
      );
      this.userService.log(activity).subscribe(() => noop());
    });

    this.SESSION_MAX_INACTIVITY_SECONDS = this.e360SessionService.maxIdleTimeoutMinutes * 60;
    this.resetInactivityTimer();

    this.SESSION_MAX_SECONDS = this.e360SessionService.sessionTimeoutMinutes * 60;
    this._maxSessionLifetimeTimerSource$ = timer(this.SESSION_MAX_SECONDS * 1000);
    this._maxSessionLifetimeTimerSource$.pipe(takeUntil(this._unsubscribe)).subscribe((val: number) => {
      if (val === 0) {
        this.logout({ message: 'ALERT_PAGE.INACTIVITY_OR_SESSION_TIME_OUT', displaySignInLink: true });
      }
    });

    this.e360SessionService.accessTokenExpirationMilliseconds$
      .pipe(takeUntil(this._unsubscribe))
      .subscribe((ms: number) => {
        if (!ms) return;
        setTimeout(() => {
          if (this.e360SessionService.isSSO) {
            this.refreshCognitoSession();
          } else {
            this.refreshAmplifySession();
          }
        }, ms);
      });

    if (this.e360SessionService.appReload) this.spinnerService.setSpinner(true);
  }

  @HostListener('click')
  @HostListener('keydown')
  handleEvent() {
    this.resetInactivityTimer();
  }

  @HostListener('window:message', ['$event'])
  onMessage({ data }: MessageEvent) {
    if (data.for === AppComms.SHELL_APP_NAME) {
      switch (data.operation) {
        case AppComms.USER_IS_ACTIVE: {
          this.resetInactivityTimer();
          break;
        }
        case AppComms.E360_HEADER_CONFIG: {
          const config: E360HeaderConfig = data.message;
          this.headerService.setHeaderConfig(config);
          if (config.renderData !== false) {
            this.stateService.sendStateToPluginApps();
          }
          break;
        }
        case AppComms.NAVIGATE_TO_APP: {
          this.handleNavigateToApp(data.message);
          break;
        }
      }
    }
  }

  private handleNavigateToApp(message: Module | { module: Module; deepLink?: DeepLink }) {
    // Supports both the legacy payload (module only) and the newer payload
    // ({ module, deepLink }) used by apps like Global Search to deep link into a specific record
    const isNestedPayload = 'module' in message;
    const module: Module = isNestedPayload ? message.module : message;
    const deepLink: DeepLink | undefined = isNestedPayload ? message.deepLink : undefined;

    // Global search spans all orgs, so a deep link may require switching org context to the
    // correct organization for the store / user navigation to render properly.
    if (!deepLink?.organizationId) {
      this.navigate(module, deepLink);
      return;
    }

    this.stateService.selectOrgById(deepLink.organizationId).subscribe({
      next: () => this.navigate(module, deepLink),
      error: err =>
        this.logger.error(
          'Aborting deep link navigation: unable to set target organization context',
          { organizationId: deepLink.organizationId },
          err
        )
    });
  }

  ngAfterViewInit() {
    this.handleDefaultModuleNavigation();
  }

  ngOnDestroy() {
    this._unsubscribe.next(true);
    this._unsubscribe.complete();
    // Note: Don't destroy the session sharing channel here as it should persist
    // across navigation within the app. It will be cleaned up when the tab/window closes.
  }

  logout({ message, nextE360Url, displaySignInLink }: SignOutOpts = {}) {
    this.modalService.dismissAll();
    this.e360SessionService.appReload = false;
    if (this.e360SessionService.isSSO) return this.cognitoService.signOut({ message, nextE360Url, displaySignInLink });

    const refreshToken = this.e360SessionService.refreshToken;
    if (refreshToken) {
      this.manageRefreshTokenThenCompleteDirectLoginSignOut(refreshToken, nextE360Url);
    } else {
      this.completeDirectLoginSignOut(nextE360Url);
    }
  }

  private manageRefreshTokenThenCompleteDirectLoginSignOut(refreshToken: string, nextE360Url?: string): void {
    // Check if other tabs have active sessions with the same refresh token
    this.sessionSharingService
      .checkForActiveSessionsInOtherTabs(refreshToken)
      .pipe(
        switchMap(hasActiveSessions => {
          if (hasActiveSessions) {
            // Other tabs are still using the same session, don't revoke the refresh token
            this.logger.info('Other tabs have active sessions with same refresh token, skipping revocation');
            return of(null); // Return empty observable to complete the chain
          } else {
            // No other active tabs with same session, safe to revoke the refresh token
            this.logger.info('No other active tabs found with same session, proceeding with refresh token revocation');
            return this.amplifyService.revokeRefreshToken(refreshToken).pipe(
              tap(() => this.logger.info('Refresh token revoked successfully')),
              catchError(err => {
                this.logger.warn('Failed to revoke refresh token, proceeding with sign-out anyway', {}, err);
                return of(null); // Continue even if revocation fails
              })
            );
          }
        }),
        catchError(err => {
          // If check fails, err on the side of caution and don't revoke
          this.logger.warn('Failed to check for active sessions, skipping token revocation', {}, err);
          return of(null);
        }),
        finalize(() => this.completeDirectLoginSignOut(nextE360Url))
      )
      .subscribe();
  }

  private completeDirectLoginSignOut(nextE360Url?: string) {
    // Amplify tokens were already cleared after initial login
    this.amplifyService.cleanStorageAfterSignOut();
    this.stateService.clear();
    this.e360SessionService.clearSessionTokens();
    // clear out org / session details if different user logs w/out refreshing app
    this.e360SessionService.clearSessionDetails();
    if (nextE360Url) {
      window.open(nextE360Url, '_self');
    } else {
      // org (idpexternalid) param needed to support direct login for multi-org users
      const orgIdpExtId = this.e360SessionService.orgIdpExternalId;
      this.router.navigate(['/signin'], {
        queryParams: {
          ...(orgIdpExtId ? { organization: orgIdpExtId } : {}),
          signedOut: 'true' // Signal explicit sign-out to prevent session restoration
        }
      });
    }
  }

  navigate(module: Module, deepLink?: DeepLink) {
    const userScope = this.e360UserService.user.scopeRoles?.[0].scope;
    const canAccessModule: boolean =
      userScope?.type === ScopeType.GLOBAL ||
      userScope?.value?.organizations?.map(org => org.id).includes(module.organizationId);
    if (!canAccessModule) {
      const websiteBaseUrlForModule: string = this.envService.isLocal
        ? 'https://localhost:4200'
        : module.websiteBaseUrl;
      const nextOrgUrlForModule: string = `${websiteBaseUrlForModule}?organization=${module.idpExternalId}&module=${module.id}`;
      this.logout({ message: '', nextE360Url: nextOrgUrlForModule });
      return;
    }

    if (module.type === ModuleType.EMBEDDED) {
      this.e360UserService.user.modules.forEach(m => (m.active = false));
    }
    this.sendModuleAccessLogAndNavigate(module, deepLink ? { deepLink } : undefined);
  }

  resetInactivityTimer() {
    if (this._inactivityTimer) {
      this._inactivityTimer.unsubscribe();
    }

    this._inactivityTimerSource$ = timer(this.SESSION_MAX_INACTIVITY_SECONDS * 1000);
    this._inactivityTimer = this._inactivityTimerSource$.pipe(takeUntil(this._unsubscribe)).subscribe((val: number) => {
      if (val === 0) {
        this.logout({ message: 'ALERT_PAGE.INACTIVITY_OR_SESSION_TIME_OUT', displaySignInLink: true });
      }
    });
  }

  private handleDefaultModuleNavigation() {
    const defaultModule = this.e360UserService.getDefaultModule(
      this.urlParams.moduleId,
      this.e360SessionService.organizationDefaultModuleId
    );
    const orgId = this.e360UserService.user.organizationId;

    if (!defaultModule || (!this.e360UserService.isMultiOrgUser && defaultModule.organizationId !== orgId)) return;

    this.sendModuleAccessLogAndNavigate(defaultModule, { defaultNavigation: true });

    const { homeModule } = this.e360UserService;
    if (defaultModule.type === ModuleType.TOP && homeModule?.organizationId === orgId) {
      this.sendModuleAccessLogAndNavigate(homeModule);
    }
  }

  private refreshCognitoSession() {
    this.cognitoService
      .refreshTokens(this.e360SessionService.refreshToken)
      .subscribe((tokens: SessionToken) => this.e360SessionService.refreshSessionWithNewTokens(tokens));
  }

  private refreshAmplifySession() {
    // Direct Login now uses SSO-style HTTP token refresh
    // No longer uses Amplify's fetchAuthSession after initial authentication
    this.amplifyService.refreshTokens(this.e360SessionService.refreshToken).subscribe({
      next: (tokens: SessionToken) => {
        this.e360SessionService.refreshSessionWithNewTokens(tokens);
      },
      error: e => {
        this.logger.error(
          `Unable to refresh token for direct login username: ${this.e360UserService.user.userName}`,
          {},
          e
        );
      }
    });
  }

  private sendModuleAccessLogAndNavigate(module: Module, opts?: NavigationOptions) {
    const { userName } = this.e360UserService.user;
    const { functionName, functionId } = this.urlParams;
    const deepLinkNav = !!(opts?.defaultNavigation && functionName);

    const activityType = deepLinkNav ? 'MODULE_ACCESSED_VIA_E360_DEEP_LINK' : 'MODULE_ACCESSED';
    let activityMsg = `${userName} accessed ${module.displayName} through e360`;

    if (deepLinkNav) {
      activityMsg += ` via a deep link with function ${functionName}${functionId ? ` and id ${functionId}` : ''}`;
    }

    const { selectedOrg, selectedOrgStore } = this.stateService;
    const activity: UserActivity = this.userActivityBuilder.buildUserActivityEvent(
      activityType,
      activityMsg,
      true,
      module.displayName,
      this.e360UserService.user,
      selectedOrg,
      selectedOrgStore
    );

    this.userService.log(activity).subscribe(() => noop());
    this.navigationService.navigate(module, opts || undefined);
  }
}
